"use client";

/**
 * Faire uses Million Lint (APM) to monitor React performance
 * in Sandbox and Staging environments. Join the #faire-million
 * channel on Slack for more information.
 */
import { init, MillionLintProvider } from "@million/lint/runtime";

// This must be first, because it sets up the global lookup.
// eslint-disable-next-line import/no-unassigned-import
import "./initialization/clientComponentSSR";

import { IntlProvider } from "@faire/web/common/localization/IntlProvider";
import { LocalizationProvider } from "@faire/web/common/localization/useLocalization";
import { NoSSR } from "@faire/web/common/NoSSR";
import { QueryClientProvider } from "@faire/web/common/reactQuery";
import { ModalStyle } from "@faire/web/slate/deprecated/Modal/GlobalModalStyle";
import { FontStyle } from "@faire/web/slate/Font/GlobalFontStyle";
import { DefaultTheme } from "@faire/web/slate/Theme";
import { ThemeProvider } from "@faire/web/slate/Theme/ThemeProvider";
import { GlobalStyleResets } from "@faire/web/ui/GlobalStyleResets";
import { ObservableWindowStoreProvider } from "@faire/web/ui/hooks/useObservableWindow";
import { MemoryHistory } from "history";
import React, { useMemo } from "react";

import { useMemoryHistory } from "@faire/retailer/app/_lib/routing/useMemoryHistory";
import { AppEffects } from "@faire/retailer/components/App/AppEffects";
import { DevTools } from "@faire/retailer/components/App/DevTools";
import { EncapsulatedMobileAppSnackbarWrapper } from "@faire/retailer/components/App/EncapsulatedMobileAppSnackbarWrapper";
import { HCaptcha } from "@faire/retailer/components/AppSignIn/HCaptcha";
import { AddedToBagContextStoreProvider } from "@faire/retailer/components/Notifications/AddedToBag/AddedToBagStore";
import { GlobalStyle } from "@faire/retailer/globalStyles";
import { RetailerRoutingContextProvider } from "@faire/retailer/RetailerRoutingContextProvider";
import { getSettingsValues } from "@faire/retailer/serialized-data/getSettingsValues";
import { ReactQueryClientStoreProvider } from "@faire/retailer/services/ReactQueryClient";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { SettingsContextProvider } from "@faire/retailer/settings/__internal__/useSetting";
import { HCaptchaStoreProvider } from "@faire/retailer/stores/domain/HCaptchaStore";
import { AppSnackbarProvider } from "@faire/retailer/stores/ui/AppSnackbarStore";

import { Globals } from "./Globals";
import { AppInitializer } from "./initialization/AppInitializer";
import { ReactQueryDevtools } from "./ReactQueryDevtools";
import { NextPrefetchContextProvider } from "./routing/NextPrefetchContext";
import { ReactRouterCompat } from "./routing/ReactRouterCompat";

/**
 * Million Lint (APM) will only run within Cypress environments
 */
// eslint-disable-next-line no-process-env
if (process.env.NODE_ENV === "production") {
  init({ testOnly: true });
}

const BaseProviders = ({
  children,
  memoryHistory,
}: {
  children: React.ReactNode;
  memoryHistory: MemoryHistory;
}) => {
  const settings = useMemo(() => getSettingsValues(), []);
  return (
    <SettingsContextProvider settings={settings}>
      <NextPrefetchContextProvider>
        <LocalizationProvider>
          <RetailerRoutingContextProvider>
            <ReactRouterCompat memoryHistory={memoryHistory}>
              <ObservableWindowStoreProvider>
                <FontStyle />
                <GlobalStyleResets />
                <GlobalStyle />
                <ModalStyle />
                <ThemeProvider theme={DefaultTheme}>
                  <QueryClientProvider>
                    <ReactQueryClientStoreProvider>
                      <HCaptchaStoreProvider>
                        <AddedToBagContextStoreProvider>
                          <AppSnackbarProvider>
                            <IntlProvider>
                              <AppEffects />
                              {children}
                              <NoSSR>
                                <HCaptcha />
                              </NoSSR>
                              <EncapsulatedMobileAppSnackbarWrapper />
                              <DevTools />
                            </IntlProvider>
                          </AppSnackbarProvider>
                        </AddedToBagContextStoreProvider>
                      </HCaptchaStoreProvider>
                    </ReactQueryClientStoreProvider>
                    <ReactQueryDevtools initialIsOpen={false} />
                  </QueryClientProvider>
                </ThemeProvider>
              </ObservableWindowStoreProvider>
            </ReactRouterCompat>
          </RetailerRoutingContextProvider>
        </LocalizationProvider>
      </NextPrefetchContextProvider>
    </SettingsContextProvider>
  );
};

export const Providers = ({
  children,
  userAgent,
  referer,
  globals,
  originalUrl,
}: {
  children: React.ReactNode;
  userAgent: string | null;
  referer: string | null;
  globals: Record<string, unknown>;
  originalUrl: string;
}) => {
  const memoryHistory = useMemoryHistory();
  return (
    <MillionLintProvider>
      <AppInitializer />
      <Globals
        globals={globals}
        userAgent={userAgent}
        referer={referer}
        memoryHistory={memoryHistory}
        originalUrl={originalUrl}
      >
        <BaseProviders memoryHistory={memoryHistory}>{children}</BaseProviders>
      </Globals>
    </MillionLintProvider>
  );
};
